import React, { useState } from 'react';
import { Play, Check, Eye, Hand, Settings, Volume2, Sliders, ChevronRight } from 'lucide-react';
export const VRTutorials = () => {
  const [completedTutorials, setCompletedTutorials] = useState([1]);
  const tutorials = [{
    id: 1,
    title: 'Basic VR Controls',
    description: 'Learn how to navigate and interact in virtual reality',
    duration: '2:45',
    icon: <Hand size={16} className="text-pink-400" data-id="element-22875" />,
    image: 'https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?q=80&w=2070&auto=format&fit=crop'
  }, {
    id: 2,
    title: 'Intimate Interactions',
    description: 'Discover how to engage with your AI companion in VR',
    duration: '4:20',
    icon: <Heart size={16} className="text-pink-400" data-id="element-22876" />,
    image: 'https://images.unsplash.com/photo-1626379953822-baec19c3accd?q=80&w=2070&auto=format&fit=crop'
  }, {
    id: 3,
    title: 'Toy Synchronization',
    description: 'Connect and calibrate haptic devices for immersive experiences',
    duration: '3:15',
    icon: <Sliders size={16} className="text-pink-400" data-id="element-22877" />,
    image: 'https://images.unsplash.com/photo-1633209245807-42a7eb62d146?q=80&w=2070&auto=format&fit=crop'
  }, {
    id: 4,
    title: 'Voice Commands',
    description: 'Control your experience hands-free with voice instructions',
    duration: '2:30',
    icon: <Volume2 size={16} className="text-pink-400" data-id="element-22878" />,
    image: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?q=80&w=2074&auto=format&fit=crop'
  }];
  const isCompleted = id => completedTutorials.includes(id);
  const markCompleted = id => {
    if (!isCompleted(id)) {
      setCompletedTutorials([...completedTutorials, id]);
    }
  };
  return <div className="space-y-6" data-id="element-22879">
      <div data-id="element-22880">
        <h3 className="text-lg font-medium mb-2" data-id="element-22881">VR Tutorials</h3>
        <p className="text-sm text-gray-400 mb-4" data-id="element-22882">
          Learn how to get the most out of your VR experience with these
          interactive guides
        </p>
        <div className="space-y-3" data-id="element-22883">
          {tutorials.map(tutorial => <div key={tutorial.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden" data-id="element-22884">
              <div className="flex" data-id="element-22885">
                <div className="w-1/3 aspect-video relative" data-id="element-22886">
                  <img src={tutorial.image} alt={tutorial.title} className="w-full h-full object-cover" data-id="element-22887" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center" data-id="element-22888">
                    <button className="w-10 h-10 rounded-full bg-pink-600/80 flex items-center justify-center backdrop-blur-sm" data-id="element-22889">
                      <Play size={18} className="text-white ml-0.5" data-id="element-22890" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 p-3 flex flex-col justify-between" data-id="element-22891">
                  <div data-id="element-22892">
                    <div className="flex items-center gap-2" data-id="element-22893">
                      {tutorial.icon}
                      <h4 className="font-medium" data-id="element-22894">{tutorial.title}</h4>
                      {isCompleted(tutorial.id) && <div className="ml-auto px-2 py-0.5 bg-green-500/20 rounded-full flex items-center gap-1 text-green-400 text-xs" data-id="element-22895">
                          <Check size={10} data-id="element-22896" />
                          <span data-id="element-22897">Completed</span>
                        </div>}
                    </div>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-22898">
                      {tutorial.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-22899">
                    <div className="text-xs text-gray-400" data-id="element-22900">
                      {tutorial.duration}
                    </div>
                    <button className="px-3 py-1 text-xs rounded-full bg-pink-500/20 text-pink-400" onClick={() => markCompleted(tutorial.id)} data-id="element-22901">
                      {isCompleted(tutorial.id) ? 'Replay' : 'Start Tutorial'}
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-id="element-22902">
        <div className="flex items-center gap-2 mb-4" data-id="element-22903">
          <Settings size={18} className="text-pink-400" data-id="element-22904" />
          <h3 className="text-lg font-medium" data-id="element-22905">Comfort Settings</h3>
        </div>
        <div className="space-y-4" data-id="element-22906">
          <div data-id="element-22907">
            <div className="flex items-center justify-between mb-1" data-id="element-22908">
              <h4 className="text-sm font-medium" data-id="element-22909">Movement Type</h4>
              <span className="text-xs text-gray-400" data-id="element-22910">Teleport</span>
            </div>
            <div className="grid grid-cols-3 gap-2" data-id="element-22911">
              <button className="px-2 py-1.5 bg-pink-600 rounded-lg text-white text-xs font-medium" data-id="element-22912">
                Teleport
              </button>
              <button className="px-2 py-1.5 bg-gray-700 rounded-lg text-gray-300 text-xs" data-id="element-22913">
                Smooth
              </button>
              <button className="px-2 py-1.5 bg-gray-700 rounded-lg text-gray-300 text-xs" data-id="element-22914">
                Dash
              </button>
            </div>
          </div>
          <div data-id="element-22915">
            <div className="flex items-center justify-between mb-1" data-id="element-22916">
              <h4 className="text-sm font-medium" data-id="element-22917">Rotation</h4>
              <span className="text-xs text-gray-400" data-id="element-22918">Snap (30°)</span>
            </div>
            <div className="grid grid-cols-3 gap-2" data-id="element-22919">
              <button className="px-2 py-1.5 bg-pink-600 rounded-lg text-white text-xs font-medium" data-id="element-22920">
                Snap (30°)
              </button>
              <button className="px-2 py-1.5 bg-gray-700 rounded-lg text-gray-300 text-xs" data-id="element-22921">
                Snap (45°)
              </button>
              <button className="px-2 py-1.5 bg-gray-700 rounded-lg text-gray-300 text-xs" data-id="element-22922">
                Smooth
              </button>
            </div>
          </div>
          <div data-id="element-22923">
            <div className="flex items-center justify-between mb-1" data-id="element-22924">
              <h4 className="text-sm font-medium" data-id="element-22925">Vignette</h4>
              <div className="w-8 h-4 bg-pink-900/30 rounded-full relative" data-id="element-22926">
                <div className="absolute inset-0 m-0.5 bg-pink-500 rounded-full left-0 right-1/2" data-id="element-22927"></div>
              </div>
            </div>
            <p className="text-xs text-gray-400" data-id="element-22928">
              Adds a subtle darkening around your peripheral vision to reduce
              motion sickness
            </p>
          </div>
          <div data-id="element-22929">
            <div className="flex items-center justify-between mb-1" data-id="element-22930">
              <h4 className="text-sm font-medium" data-id="element-22931">Height Adjustment</h4>
              <span className="text-xs text-gray-400" data-id="element-22932">Auto</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-22933">
              <span className="text-xs text-gray-400" data-id="element-22934">-0.5m</span>
              <input type="range" min="0" max="100" defaultValue="50" className="flex-1 accent-pink-500" data-id="element-22935" />
              <span className="text-xs text-gray-400" data-id="element-22936">+0.5m</span>
            </div>
          </div>
          <div data-id="element-22937">
            <div className="flex items-center justify-between mb-1" data-id="element-22938">
              <h4 className="text-sm font-medium" data-id="element-22939">Comfort Mode</h4>
              <div className="w-8 h-4 bg-pink-900/30 rounded-full relative" data-id="element-22940">
                <div className="absolute inset-0 m-0.5 bg-pink-500 rounded-full left-1/2 right-0" data-id="element-22941"></div>
              </div>
            </div>
            <p className="text-xs text-gray-400" data-id="element-22942">
              Enables all comfort settings recommended for new VR users
            </p>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-xl border border-pink-800/50 p-4" data-id="element-22943">
        <div className="flex items-center gap-3" data-id="element-22944">
          <div className="w-10 h-10 flex items-center justify-center bg-pink-500/20 rounded-full text-pink-400" data-id="element-22945">
            <Eye size={18} data-id="element-22946" />
          </div>
          <div data-id="element-22947">
            <h3 className="font-medium" data-id="element-22948">Need more help?</h3>
            <p className="text-sm text-gray-300" data-id="element-22949">
              Our VR specialists can guide you through personalized setup
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between" data-id="element-22950">
          <div className="text-xs text-gray-400" data-id="element-22951">Available 24/7</div>
          <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white text-sm font-medium flex items-center gap-1" data-id="element-22952">
            <span data-id="element-22953">Contact Support</span>
            <ChevronRight size={14} data-id="element-22954" />
          </button>
        </div>
      </div>
    </div>;
};